/**
 * Consent state for analytics. Honors Do-Not-Track and Global Privacy Control.
 * No analytics module fires until consent === "granted".
 */
export type ConsentState = "granted" | "denied" | "unknown";

const KEY = "ccb-analytics-consent";

interface NavigatorGpc extends Navigator {
  globalPrivacyControl?: boolean;
}

function automaticDenial(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.doNotTrack === "1") return true;
  if ((navigator as NavigatorGpc).globalPrivacyControl === true) return true;
  return false;
}

export function getConsent(): ConsentState {
  if (automaticDenial()) return "denied";
  if (typeof window === "undefined") return "unknown";
  const v = window.localStorage.getItem(KEY);
  if (v === "granted" || v === "denied") return v;
  return "unknown";
}

const listeners = new Set<(s: ConsentState) => void>();

export function setConsent(state: "granted" | "denied"): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(KEY, state);
    } catch {
      /* ignore */
    }
  }
  for (const l of listeners) l(state);
}

export function onConsentChange(listener: (s: ConsentState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clears the persisted state. */
export function _resetConsent(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }
}
